import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { TelekitModal } from '../../components/modal/TelekitModal'
import {
  activateTelekit,
  deactivateTelekit,
  deleteTelekit,
  getTelekitById,
} from '../../core/_requests'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { TelekitList } from '../../core/_models'
import { ModalSize, showAlertLoading, showConfirmAlert, showError } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { RowActionItem, RowActions, useModal, useToast } from 'app/components'
import { TransferirCustodiaModal } from '../../../detalhe/TransferirCustodiaModal'

type Props = {
  telekit: TelekitList
}

export const TelekitsActionsCell: FC<Props> = ({ telekit }) => {
  const { showModal } = useModal()
  const { showToast } = useToast()
  const { refetch } = useQueryResponse()
  const navigate = useNavigate()
  const { t } = useT()
  const nome = telekit.padrao
    ? t('dispositivos.rotulos.maleta_padrao')
    : telekit.identificador
    ? `Telekit ${telekit.identificador}`
    : 'Telekit'
  // A Padrão não pode ser alterada, inativada nem excluída (mesma frase do backend).
  const motivoPadrao = telekit.padrao
    ? t('pagina.telekits.dispositivos.padrao_bloqueada')
    : undefined

  const openEditModal = () =>
    showModal({
      title: 'Editar Telekit',
      content: <TelekitModal />,
      size: ModalSize.Medium,
      fetchers: {
        telekit: () => getTelekitById(telekit.id),
      },
    })

  const openCustodia = () =>
    showModal({
      title: t('pagina.telekits.custodia.titulo'),
      description: nome,
      content: <TransferirCustodiaModal />,
      size: ModalSize.Medium,
      fetchers: {
        telekit: () => getTelekitById(telekit.id),
      },
    })

  const setStatus = async (ativo: boolean) => {
    try {
      showAlertLoading()
      ativo ? await activateTelekit(telekit.id) : await deactivateTelekit(telekit.id)
      Swal.close()
      showToast({ variant: 'success', title: `${nome} foi ${ativo ? 'ativado' : 'inativado'}.` })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  // Inativar pede confirmação; ativar é reversível e não pede.
  const askInativar = async () => {
    const confirmed = await showConfirmAlert({
      title: 'Inativar telekit?',
      text: `${nome} ficará inativo. Você pode reativá-lo quando quiser.`,
      confirmButtonText: 'Inativar',
      cancelButtonText: 'Cancelar',
      danger: true,
    })
    if (confirmed) await setStatus(false)
  }

  const askDelete = async () => {
    const confirmed = await showConfirmAlert({
      title: 'Excluir telekit?',
      text: `${nome} será excluído. Para só tirá-lo de uso, prefira inativar.`,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      danger: true,
    })
    if (!confirmed) return

    try {
      showAlertLoading()
      await deleteTelekit(telekit.id)
      Swal.close()
      showToast({ variant: 'success', title: `${nome} foi excluído.` })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  const items: RowActionItem[] = [
    { label: 'Editar', onClick: openEditModal, disabledReason: motivoPadrao },
    { label: t('pagina.telekits.custodia.acao'), onClick: openCustodia },
    telekit.ativo
      ? { label: 'Inativar', onClick: askInativar, disabledReason: motivoPadrao, divider: true }
      : { label: 'Ativar', onClick: () => setStatus(true), divider: true },
    { label: 'Excluir', onClick: askDelete, danger: true, disabledReason: motivoPadrao },
  ]

  return (
    <RowActions
      itemName={nome}
      primaryAction={{
        label: t('pagina.telekits.dispositivos.acao_dispositivos'),
        onClick: () => navigate(`/apps/admin/telekits/${telekit.id}`),
      }}
      items={items}
    />
  )
}
